import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Check, MessageCircle, CalendarCheck } from "lucide-react";
import { WHATSAPP_URL, scrollToId } from "../constants";

const MaskedLine = ({ children, delay, reduce }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block"
      initial={reduce ? false : { y: "110%" }}
      animate={reduce ? {} : { y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const TRUST_ITEMS = ["Zuverlässig", "Gründlich", "Professionell", "Individuelle Reinigungskonzepte"];

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 110]);
  const fade = (delay) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
        };

  return (
    <section
      id="start"
      ref={ref}
      className="relative min-h-[92vh] flex items-center bg-navy overflow-hidden pt-[72px]"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-28 h-[calc(100%+7rem)]">
        <img
          src="https://images.pexels.com/photos/6794970/pexels-photo-6794970.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Professionelle Gebäudereinigung in einem modernen Bürogebäude"
          className="w-full h-full object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-navy/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-navy/30" />
      <div className="noise-overlay" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 w-full">
        <motion.p
          {...fade(0.05)}
          className="text-ice/80 text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase mb-7 flex items-center gap-4"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-conv inline-block" />
          Ihr Reinigungspartner für Lehrte und Umgebung
        </motion.p>

        <h1
          className="font-heading font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl"
          data-testid="hero-headline"
        >
          <MaskedLine delay={0.15} reduce={reduce}>Professionelle</MaskedLine>
          <MaskedLine delay={0.28} reduce={reduce}>Gebäudereinigung</MaskedLine>
          <MaskedLine delay={0.41} reduce={reduce}>
            <span className="text-ice">für Unternehmen.</span>
          </MaskedLine>
        </h1>

        <motion.p
          {...fade(0.6)}
          className="mt-7 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed"
          data-testid="hero-subheadline"
        >
          Sauberkeit, die man sieht. Zuverlässigkeit, auf die Sie sich verlassen können.
        </motion.p>

        <motion.div {...fade(0.75)} className="mt-10 flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => scrollToId("kontakt")}
            className="inline-flex items-center justify-center gap-2.5 rounded-full bg-conv hover:bg-conv-dark text-white font-semibold px-8 py-4 text-base transition-all duration-200 hover:scale-[1.03] shadow-[0_16px_32px_-12px_rgba(16,185,129,0.55)]"
            data-testid="hero-cta-besichtigung"
          >
            <CalendarCheck className="w-5 h-5" />
            Kostenlose Objektbesichtigung
          </button>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold px-8 py-4 text-base transition-all duration-200 hover:scale-[1.03]"
            data-testid="hero-cta-whatsapp"
          >
            <MessageCircle className="w-5 h-5" />
            Jetzt per WhatsApp anfragen
          </a>
        </motion.div>

        <motion.ul
          {...fade(0.9)}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
          data-testid="hero-trust-list"
        >
          {TRUST_ITEMS.map((item) => (
            <li key={item} className="flex items-center gap-2 text-sm text-white/85 font-medium">
              <Check className="w-4 h-4 text-conv shrink-0" strokeWidth={3} />
              {item}
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
};
